import React, { useState, Fragment } from 'react';
import { Link } from 'react-router-dom';

import {
  DataTypeProvider,
  DataTypeProviderProps,
  IntegratedSorting,
  SortingState,
} from '@devexpress/dx-react-grid';

import {
  Grid,
  Table,
  TableHeaderRow,
  TableColumnResizing,
} from '@devexpress/dx-react-grid-bootstrap4';

import '@devexpress/dx-react-grid-bootstrap4/dist/dx-react-grid-bootstrap4.css';

import { ProductUpload } from '../../types/ProductUpload';

import Tabs from '../../components/Tabs';
import { useGetQuery } from 'api/useGetQuery';
import { ENDPOINTS } from 'api/endpoints';
import { useDataEnvironment } from 'contexts/DataEnvironmentContext';
import PoweredByHeader from 'components/PoweredByHeader';
import { paths, routes } from 'routes';
import ConfirmModal from 'components/ConfirmModal/ConfirmModal';
import { useMutation, useQueryClient } from 'react-query';
import { showError } from 'utils/showError';
import { showSuccess } from 'utils/showSuccess';
import { axiosClient } from 'api/axiosClient';

import { DateTypeProvider } from 'components/DataGrid/DataTypeProviders/DateTypeProvider';
import { Button } from 'components/Button';
import { LoadingDataIndicator } from 'components/DataGrid/components/LoadingDataIndicator';
import { Refresh } from 'components/Icons/Refresh';
import { dateISOStringToFormat, dateToFormat, MONTH_YEAR_FORMAT } from 'utils/formatters/date';
import { errorMessage } from 'utils/errorMessage';

type DataTypeFormatterComponent = React.FC<DataTypeProvider.ValueFormatterProps>;

type ProductUploadValueFormatter = Omit<DataTypeProvider.ValueFormatterProps, 'row'> & {
  row: ProductUpload;
};

const ProductUploads: React.FC = () => {
  const queryClient = useQueryClient();

  const [dataEnvironment] = useDataEnvironment();
  const [selectedUpload, setSelectedUpload] = useState<ProductUpload>();
  const [showDeleteConfirmation, setShowDeletConfirmation] = useState<boolean>(false);
  const [lastRefreshedAt, setLastRefreshedAt] = useState<Date>();

  const columns = [
    { name: 'name', title: 'Name of Upload' },
    { name: 'uploadedBy', title: 'Uploaded by' },
    { name: 'uploadedAt', title: 'Date & Time Uploaded' },
    { name: 'status', title: 'Status of Sync' },
    { name: 'monthsSynced', title: 'Months Synced' },
    { name: 'actions', title: 'Actions' },
  ];

  const [defaultColumnWidths] = useState(
    columns.map((column) => ({
      columnName: column.name,
      width: (window.innerWidth - 50) / columns.length,
    })),
  );

  const productUploadsListEndpoint = ENDPOINTS.productUploads(dataEnvironment!.id);

  const { isFetching, data } = useGetQuery<ProductUpload[]>(productUploadsListEndpoint, {
    onSuccess: () => {
      setLastRefreshedAt(new Date());
    },
  });

  const deleteUpload = useMutation(
    (id: string) => axiosClient.delete(ENDPOINTS.productUpload(dataEnvironment!.id, id)),
    {
      onError: (error: any) => {
        showError(
          errorMessage({
            error,
            defaultMessage: 'An error occurred when attempting to delete the upload.',
          }),
        );
      },
      onSuccess: () => {
        showSuccess('Successfully deleted the upload.');
        queryClient.invalidateQueries(productUploadsListEndpoint);
      },
    },
  );

  const handleDeleteUpload = (upload: ProductUpload) => {
    setSelectedUpload(upload);
    setShowDeletConfirmation(true);
  };

  const ActionsFormatter: React.FC<ProductUploadValueFormatter> = ({
    row: upload,
  }: ProductUploadValueFormatter) => {
    const isProductUploadActionable = upload.status === 'Completed' || upload.status === 'Failed';
    return isProductUploadActionable ? (
      <Fragment>
        <Link to={routes.getEditUploadRoute(upload.id)} className="btn btn-link p-0 mr-2">
          Edit
        </Link>

        <button className="btn btn-link p-0" onClick={() => handleDeleteUpload(upload)}>
          Delete
        </button>
      </Fragment>
    ) : null;
  };

  const DateRangeFormatter: React.FC<ProductUploadValueFormatter> = ({
    row: data,
  }: ProductUploadValueFormatter) => {
    const upload = data as ProductUpload;
    const start = upload.startDate && dateISOStringToFormat(upload.startDate, MONTH_YEAR_FORMAT);
    const end = upload.endDate && dateISOStringToFormat(upload.endDate, MONTH_YEAR_FORMAT);

    return <span>{start === end ? start : `${start} - ${end}`}</span>;
  };

  const ActionsTypeProvider: React.FC<DataTypeProviderProps> = (props: DataTypeProviderProps) => (
    <DataTypeProvider
      formatterComponent={ActionsFormatter as DataTypeFormatterComponent}
      {...props}
    />
  );

  const DateRangeTypeProvider: React.FC<DataTypeProviderProps> = (props: DataTypeProviderProps) => (
    <DataTypeProvider
      formatterComponent={DateRangeFormatter as DataTypeFormatterComponent}
      {...props}
    />
  );

  const sortingStateColumnExtensions = [{ columnName: 'actions', sortingEnabled: false }];

  const cellValue = (row: any, columnName: string) => {
    if (columnName !== 'monthsSynced') {
      return row[columnName];
    }

    return row.startDate;
  };

  return (
    <>
      <span className="iconify" data-icon="oi-arrow-thick-top" data-inline="false"></span>
      <PoweredByHeader />
      <Tabs />

      <ConfirmModal
        show={showDeleteConfirmation}
        onClose={() => {
          setShowDeletConfirmation(false);
        }}
        onConfirm={() => {
          setShowDeletConfirmation(false);
          deleteUpload.mutate(selectedUpload!.id);
        }}
        title="Delete Product Upload"
        message="Are you sure you want to delete this upload?"
      />

      <div className="mb-0 text-right">
        <div>
          <a
            href="https://app.evestment.com/Analytics"
            rel="noopener noreferrer"
            target="_blank"
            className="btn btn-link">
            View in eVestment
          </a>
          <Button
            className={'mr-2 pl-0 refreshWidth'}
            loading={isFetching}
            variant="secondary"
            onClick={() => {
              queryClient.invalidateQueries(productUploadsListEndpoint);
            }}>
            <>
              {!isFetching && (
                <span
                  style={{ display: 'inline-block', width: 24, marginLeft: -5, marginRight: 5 }}>
                  <Refresh />
                </span>
              )}{' '}
              Refresh
            </>
          </Button>

          <Button to={paths.newProductUpload} variant="primary">
            New Product Upload
          </Button>
        </div>
        <small>{lastRefreshedAt && `Last refreshed ${dateToFormat(lastRefreshedAt)}`}</small>
      </div>

      <Grid rows={data || []} columns={columns} getCellValue={cellValue}>
        <DateTypeProvider for={['uploadedAt']} />
        <ActionsTypeProvider for={['actions']} />
        <DateRangeTypeProvider for={['monthsSynced']} />
        <SortingState
          columnExtensions={sortingStateColumnExtensions}
          defaultSorting={[{ columnName: 'uploadedAt', direction: 'desc' }]}
        />
        <IntegratedSorting />
        <Table
          noDataCellComponent={
            isFetching || !columns.length ? LoadingDataIndicator : Table.NoDataCell
          }
        />
        <TableColumnResizing defaultColumnWidths={defaultColumnWidths} />
        <TableHeaderRow showSortingControls />
      </Grid>
    </>
  );
};

export default ProductUploads;
